<template>
  <div class="edit-bulk-submission">
    <slot name="header" />
    <v-row justify="center" align-content="stretch">
      <v-col cols="2">
        <SummaryMetric title="Total Claims" :value="mappedRows.length" />
      </v-col>
      <v-col cols="2">
        <SummaryMetric title="Total Main Claimants" :value="totalMainClaimants" />
      </v-col>
      <v-col cols="2">
        <SummaryMetric title="Valid Claims" :value="validClaims.length" />
      </v-col>
      <v-col cols="2">
        <SummaryMetric
          title="Invalid Claims"
          :value="invalidClaims.length"
          :error="invalidClaims.length > 0"
          :success="invalidClaims.length === 0"
        />
      </v-col>
    </v-row>
    <v-row justify="center" class="mt-4" v-if="validClaims.length > 0">
      <v-col cols="8">
        <ReadyToSubmitAlert
          :valid-claims="validClaims"
          :has-invalid-claims="invalidClaims.length > 0"
          :loading="submitLoading"
          @submit-valid-claims="submitBulkClaims"
        />
      </v-col>
    </v-row>
    <v-progress-circular v-if="loading" indeterminate color="primary" />
    <template v-else>
      <v-expansion-panels flat multiple :value="invalidClaimsPanelIndexToExpand">
        <v-expansion-panel v-if="validClaims.length > 0">
          <v-expansion-panel-header>
            <Typography variant="subtitle-1">Valid Claims ({{ validClaims.length }})</Typography>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <BulkClaimsTable :columns="columns" :mappedRows="validClaims" />
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel v-if="invalidClaims.length > 0">
          <v-expansion-panel-header>
            <Typography variant="subtitle-1"
              >Invalid Claims ({{ invalidClaims.length }})</Typography
            >
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="invalid-claims-container">
              <div class="d-flex align-center mb-2 invalid-claims-actions">
                <Button
                  @click="reValidateAll"
                  outlined
                  :loading="revalidateLoading"
                  :disabled="mappedRows.length === 0"
                  >Re-Validate All</Button
                >
                <v-spacer />
                <Button text @click="downloadInvalidClaims">Download</Button>
              </div>

              <v-expansion-panels
                flat
                multiple
                class="invalid-claims-panels"
                :value="invalidEmailGroupPanelsExpanded"
              >
                <v-expansion-panel
                  v-for="(claims, email) in invalidClaimsGroupedByEmail"
                  :key="email"
                  class="invalid-claims-group"
                >
                  <v-expansion-panel-header>
                    <div>
                      <Typography
                        v-if="
                          email !== unknownEmailKey &&
                          Array.isArray(claims) &&
                          claims.length > 0 &&
                          isValidEmail(claims[0].id)
                        "
                        variant="subtitle-1"
                        >{{ email }}</Typography
                      >
                      <Typography v-else variant="subtitle-1">Unknown</Typography>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-text-field
                      v-if="
                        Array.isArray(claims) &&
                        claims.length > 0 &&
                        (email === unknownEmailKey || !isValidEmail(claims[0].id))
                      "
                      v-model="emailInputs[email]"
                      label="Submitter Email"
                      placeholder="Enter submitter email"
                      :error="!emailInputs[email]"
                      :error-messages="
                        Array.isArray(claims) && claims[0]
                          ? getEmailErrorMessage(email, claims[0].id)
                          : ''
                      "
                      filled
                      dense
                      @click.stop
                    />
                    <BulkClaimsTable
                      :columns="columns.filter((column) => column.value !== 'email')"
                      :mappedRows="claims"
                      :mappedRowsErrors="mappedRowsErrors"
                      deletable
                      editable
                      showErrorColumn
                      @delete-row="showDeleteRowModal"
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>

    <Modal
      :value="deleteModalVisible"
      :onClose="cancelDeleteModal"
      max-width="444px"
      :show-close-button="false"
    >
      <template #header>
        <Typography variant="h-5">Delete claim?</Typography>
      </template>
      <Typography variant="body-1" class="text-left"> This action cannot be undone. </Typography>
      <div class="d-flex justify-end pt-4">
        <Button text @click="cancelDeleteModal" :disabled="deletingRow">Cancel</Button>
        <Button text @click="confirmDeleteRow" :loading="deletingRow">Delete claim</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import SummaryMetric from "./SummaryMetric.vue";
import ReadyToSubmitAlert from "./ReadyToSubmitAlert.vue";
import BulkClaimsTable from "./BulkClaimsTable.vue";
import Button from "@/components/generic/Button.vue";
import Modal from "@/components/generic/Modal.vue";

import csvGenerationMixin from "@/mixins/csvGenerationMixin";

/** Stable array refs so v-expansion-panels :value does not reset on every render */
const OUTER_PANELS_NONE_OPEN = Object.freeze([]);
const OUTER_PANELS_FIRST_ONLY = Object.freeze([0]);
const OUTER_PANELS_SECOND_ONLY = Object.freeze([1]);

export default {
  name: "EditBulkSubmission",
  mixins: [csvGenerationMixin],
  components: {
    Typography,
    SummaryMetric,
    ReadyToSubmitAlert,
    BulkClaimsTable,
    Button,
    Modal,
  },
  props: {
    submitLoading: {
      type: Boolean,
      default: false,
    },
    mappedRows: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
    mappedRowsErrors: {
      type: Array,
      default: () => [],
    },
    onDeleteRow: {
      type: Function,
      default: null,
    },
    onRevalidateAll: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      unknownEmailKey: "unknown_email",
      invalidClaimsGroupedByEmail: {},
      emailInputs: {},
      loading: false,
      revalidateLoading: false,
      deleteModalVisible: false,
      rowToDeleteId: null,
      deletingRow: false,
      /** Group key signature for invalid-by-email; only refresh expanded indices when grouping changes */
      invalidEmailGroupsKeySig: "",
      invalidEmailGroupPanelsExpanded: [],
    };
  },
  computed: {
    validClaims() {
      return this.mappedRows.filter((row) => {
        return !this.mappedRowsErrors.find((errorRow) => errorRow.uuid === row.id);
      });
    },
    invalidClaims() {
      const invalidClaims = this.mappedRows.filter((row) =>
        this.mappedRowsErrors.find((errorRow) => errorRow.uuid === row.id)
      );
      return invalidClaims;
    },
    totalMainClaimants() {
      const uniqueEmails = new Set(this.mappedRows.map((row) => (row.email ?? "").toLowerCase()));
      return uniqueEmails.size;
    },
    /** Valid panel collapsed by default; invalid panel open whenever it exists */
    invalidClaimsPanelIndexToExpand() {
      const hasValid = this.validClaims.length > 0;
      const hasInvalid = this.invalidClaims.length > 0;
      if (!hasInvalid) {
        return OUTER_PANELS_NONE_OPEN;
      }
      if (hasValid) {
        return OUTER_PANELS_SECOND_ONLY;
      }
      return OUTER_PANELS_FIRST_ONLY;
    },
    invalidClaimsPanelsExpanded() {
      return this.invalidEmailGroupPanelsExpanded;
    },
  },
  methods: {
    mapRowsWithUpdatedEmails(rows = []) {
      return rows.map((row) => {
        const emailKey = row.email?.toLowerCase();
        const lookupKey =
          emailKey === undefined || emailKey === null || emailKey === ""
            ? this.unknownEmailKey
            : emailKey;
        const { id, ...claimData } = row;
        const nextEmail = this.emailInputs[lookupKey] ?? emailKey;
        return {
          id,
          ...claimData,
          email: nextEmail == null ? "" : String(nextEmail),
        };
      });
    },
    isValidEmail(rowUuid) {
      return (
        !this.mappedRowsErrors.find((errorRow) => errorRow.uuid === rowUuid)?.errors?.email
          ?.length > 0
      );
    },
    getEmailErrorMessage(email, rowUuid) {
      if (email === this.unknownEmailKey) {
        return "Submitter email is required";
      }
      if (!this.isValidEmail(rowUuid)) {
        const errorRow = this.mappedRowsErrors.find((errorRow) => errorRow.uuid === rowUuid);
        const emailError = errorRow?.errors?.email;
        return (Array.isArray(emailError) ? emailError[0] : emailError) || `${email} is invalid`;
      }
      return "";
    },
    async computeInvalidClaimsGroupedByEmail() {
      const emailInputs = {};
      const grouped = {};

      for (const row of this.invalidClaims) {
        const rawEmail = row.email;
        const email =
          rawEmail === undefined || rawEmail === null || rawEmail === ""
            ? this.unknownEmailKey
            : rawEmail.toLowerCase();

        if (!grouped[email]) {
          grouped[email] = [];
          if (Object.prototype.hasOwnProperty.call(this.emailInputs, email)) {
            emailInputs[email] = this.emailInputs[email];
          } else {
            // Initialize emailInputs for unknown or invalid emails
            if (email === this.unknownEmailKey) {
              emailInputs[email] = "";
            } else if (!this.isValidEmail(row.id)) {
              emailInputs[email] = rawEmail; // Pre-populate with the invalid email
            }
          }
        }
        grouped[email].push(row);
      }

      // Order by email, with unknown email key last
      const sortedKeys = Object.keys(grouped)
        .filter((k) => k !== this.unknownEmailKey)
        .sort();
      if (grouped[this.unknownEmailKey]) {
        sortedKeys.push(this.unknownEmailKey);
      }
      const ordered = {};
      for (const k of sortedKeys) {
        ordered[k] = grouped[k];
      }

      this.invalidClaimsGroupedByEmail = ordered;
      this.emailInputs = emailInputs;

      const groupKeySig = sortedKeys.join("\0");
      if (
        groupKeySig !== this.invalidEmailGroupsKeySig ||
        this.invalidEmailGroupPanelsExpanded.length !== sortedKeys.length
      ) {
        this.invalidEmailGroupsKeySig = groupKeySig;
        this.invalidEmailGroupPanelsExpanded =
          sortedKeys.length === 0 ? [] : Array.from({ length: sortedKeys.length }, (_, i) => i);
      }
    },
    submitBulkClaims() {
      const claims = this.mapRowsWithUpdatedEmails(this.mappedRows);
      this.$emit("submit-bulk-claims", { claims });
    },
    downloadInvalidClaims() {
      const invalidClaimsWithUpdatedEmails = this.mapRowsWithUpdatedEmails(this.invalidClaims);
      const csv = this.generateCSV(this.columns, invalidClaimsWithUpdatedEmails);
      this.downloadCSV(csv, "invalid-claims.csv");
    },
    showDeleteRowModal(rowId) {
      this.rowToDeleteId = rowId;
      this.deleteModalVisible = true;
    },
    cancelDeleteModal() {
      this.deleteModalVisible = false;
      this.rowToDeleteId = null;
    },
    async confirmDeleteRow() {
      this.deletingRow = true;
      try {
        if (this.onDeleteRow && this.rowToDeleteId != null) {
          await this.onDeleteRow(this.rowToDeleteId);
        }
      } catch (error) {
        this.$store.dispatch("showError", {
          message: "Error deleting row",
        });
        console.error(error);
      } finally {
        this.deletingRow = false;
        this.cancelDeleteModal();
      }
    },
    async reValidateAll() {
      if (!this.onRevalidateAll) return;

      const claims = this.mapRowsWithUpdatedEmails(this.mappedRows);

      this.revalidateLoading = true;
      try {
        await this.onRevalidateAll(claims);
      } finally {
        this.revalidateLoading = false;
      }
    },
  },
  watch: {
    mappedRows: {
      handler() {
        this.computeInvalidClaimsGroupedByEmail();
      },
      deep: true,
    },
    mappedRowsErrors: {
      handler() {
        this.computeInvalidClaimsGroupedByEmail();
      },
      deep: true,
    },
  },
  created() {
    this.computeInvalidClaimsGroupedByEmail();
  },
};
</script>

<style scoped>
.edit-bulk-submission {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1.5rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
.v-expansion-panels {
  gap: 1.5rem;
}
.v-expansion-panel {
  border-radius: 8px;
  border: 1px solid var(--System-On-Light-Stroke-Medium, rgba(0, 0, 0, 0.2));
}

.invalid-claims-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  justify-content: flex-start;
  align-items: flex-start;
}
.invalid-claims-panels {
  align-self: stretch;
  gap: 1.5rem;
}

.invalid-claims-actions {
  width: 100%;
  align-self: stretch;
}

.invalid-claims-group.v-expansion-panel {
  border: none;
  border-radius: 8px;
  background: var(--Surface-Light-Dark, #f6f5f6);
  text-align: left;
}
.email-name-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
}
</style>
